/**
 * HRIS Recruitment — Kanban board (7 stages) for managing job candidates.
 *
 * Features:
 *  - 7 columns matching RecruitmentStage enum (from the stage machine).
 *  - Drag-and-drop via @dnd-kit with ±1 adjacency enforcement.
 *  - Kebab "Déplacer vers…" menu listing valid ±1 stages (click/touch fallback).
 *  - Filters: job posting select, source, "Afficher rejetés" toggle — URL-persisted.
 *  - Right slide-over drawer: details, notes autosave, event history, stage stepper.
 *
 * Working language: French.
 */

import { useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FileText,
  Link2,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  RefreshCcw,
  Calendar,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { PageShell } from '@/shared/components';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useJobPostings } from '../api/jobsApi';
import {
  STAGES,
  STAGE_LABELS,
  SOURCE_LABELS,
  useCandidates,
  useCandidate,
  useMoveCandidate,
  useUpdateCandidate,
  type Candidate,
  type RecruitmentStage,
  type CandidateSource,
  type CandidateFilters,
} from '../api/recruitmentApi';

// ---------------------------------------------------------------------------
// Stage adjacency helpers (mirrors stage-machine.ts on the backend)
// ---------------------------------------------------------------------------

function getAdjacentStages(stage: RecruitmentStage): RecruitmentStage[] {
  const idx = STAGES.indexOf(stage);
  const result: RecruitmentStage[] = [];
  if (idx > 0) result.push(STAGES[idx - 1]);
  if (idx < STAGES.length - 1) result.push(STAGES[idx + 1]);
  return result;
}

function isTerminal(stage: RecruitmentStage): boolean {
  return stage === 'embauche';
}

function canTransition(from: RecruitmentStage, to: RecruitmentStage): boolean {
  if (isTerminal(from)) return false;
  const diff = Math.abs(STAGES.indexOf(to) - STAGES.indexOf(from));
  return diff === 1;
}

// ---------------------------------------------------------------------------
// Source badge color
// ---------------------------------------------------------------------------

const SOURCE_COLORS: Record<CandidateSource, string> = {
  indeed: 'bg-blue-100 text-blue-800 border-blue-200',
  website: 'bg-purple-100 text-purple-800 border-purple-200',
  referral: 'bg-amber-100 text-amber-800 border-amber-200',
  linkedin: 'bg-sky-100 text-sky-800 border-sky-200',
  other: 'bg-gray-100 text-gray-700 border-gray-200',
};

// ---------------------------------------------------------------------------
// Candidate card (draggable)
// ---------------------------------------------------------------------------

interface CandidateCardProps {
  candidate: Candidate;
  onOpen: (candidate: Candidate) => void;
  onMove: (id: string, stage: RecruitmentStage) => void;
  onArchive: (id: string, archived: boolean) => void;
}

function CandidateCard({ candidate, onOpen, onMove, onArchive }: CandidateCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id: candidate.id,
      disabled: isTerminal(candidate.stage) || candidate.archived,
    });

  const adjacent = getAdjacentStages(candidate.stage).filter((s) =>
    canTransition(candidate.stage, s)
  );

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const appliedAgo = candidate.appliedAt
    ? formatDistanceToNow(new Date(candidate.appliedAt), { addSuffix: true, locale: fr })
    : null;

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-no-drag]') || isDragging) return;
    onOpen(candidate);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'bg-card border rounded-lg p-3 shadow-sm cursor-grab active:cursor-grabbing',
        'min-w-0 space-y-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        candidate.archived && 'opacity-50 cursor-default'
      )}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(candidate);
        }
      }}
      aria-label={`Candidat : ${candidate.fullName ?? 'Sans nom'} — ${STAGE_LABELS[candidate.stage]}`}
      {...(candidate.archived ? {} : { ...attributes, ...listeners })}
    >
      {/* Name + kebab */}
      <div className="flex items-start justify-between gap-1">
        <p className="text-sm font-medium truncate text-foreground">
          {candidate.fullName ?? (
            <span className="italic text-muted-foreground">Sans nom</span>
          )}
        </p>

        <div
          data-no-drag
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="shrink-0 rounded p-0.5 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Menu candidat"
              >
                <MoreVertical className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {adjacent.length > 0 && !candidate.archived ? (
                <>
                  <DropdownMenuLabel className="text-xs">Déplacer vers…</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {adjacent.map((s) => (
                    <DropdownMenuItem key={s} onClick={() => onMove(candidate.id, s)}>
                      {STAGE_LABELS[s]}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                </>
              ) : null}
              <DropdownMenuItem
                className={candidate.archived ? '' : 'text-destructive'}
                onClick={() => onArchive(candidate.id, !candidate.archived)}
              >
                {candidate.archived ? 'Restaurer' : 'Rejeter'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Source badge + Réapplication badge */}
      <div className="flex flex-wrap gap-1">
        <span
          className={cn(
            'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border',
            SOURCE_COLORS[candidate.source]
          )}
        >
          {SOURCE_LABELS[candidate.source]}
        </span>
        {candidate.reappliedAt && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-800 border border-orange-200">
            <RefreshCcw className="h-2.5 w-2.5" />
            Réapplication
          </span>
        )}
      </div>

      {/* Score + applied date */}
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
        {candidate.score != null && (
          <span className="font-medium text-foreground">{candidate.score}/10</span>
        )}
        {appliedAgo && (
          <span className="flex items-center gap-0.5 ml-auto">
            <Calendar className="h-3 w-3" />
            {appliedAgo}
          </span>
        )}
      </div>

      {/* CV / question-set / debrief links */}
      {(candidate.cvUrl || candidate.cvPending || candidate.questionSetUrl || candidate.debriefUrl) && (
        <div
          className="flex flex-wrap gap-2 text-[11px]"
          data-no-drag
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {candidate.cvUrl && (
            <a
              href={candidate.cvUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-0.5 text-primary hover:underline"
              aria-label="CV du candidat"
            >
              <FileText className="h-3 w-3" /> CV
            </a>
          )}
          {candidate.cvPending && !candidate.cvUrl && (
            <span className="text-muted-foreground italic">CV en attente</span>
          )}
          {candidate.questionSetUrl && (
            <a
              href={candidate.questionSetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-0.5 text-primary hover:underline"
              aria-label="Questions de présélection"
            >
              <Link2 className="h-3 w-3" /> Questions
            </a>
          )}
          {candidate.debriefUrl && (
            <a
              href={candidate.debriefUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-0.5 text-primary hover:underline"
              aria-label="Débrief"
            >
              <Link2 className="h-3 w-3" /> Débrief
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drag overlay card (no dnd listeners — static clone during drag)
// ---------------------------------------------------------------------------

function DragCard({ candidate }: { candidate: Candidate }) {
  return (
    <div className="bg-card border rounded-lg p-3 shadow-xl w-48 space-y-1 pointer-events-none rotate-2">
      <p className="text-sm font-medium truncate">{candidate.fullName ?? 'Sans nom'}</p>
      <span
        className={cn(
          'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border',
          SOURCE_COLORS[candidate.source]
        )}
      >
        {SOURCE_LABELS[candidate.source]}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kanban column (drop zone)
// ---------------------------------------------------------------------------

interface KanbanColumnProps {
  stage: RecruitmentStage;
  candidates: Candidate[];
  onOpen: (c: Candidate) => void;
  onMove: (id: string, stage: RecruitmentStage) => void;
  onArchive: (id: string, archived: boolean) => void;
  isFirst: boolean;
}

function KanbanColumn({
  stage,
  candidates,
  onOpen,
  onMove,
  onArchive,
  isFirst,
}: KanbanColumnProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 min-w-[200px] max-w-[240px] shrink-0',
        isFirst && 'sticky left-0 z-10 bg-background pr-2'
      )}
      role="region"
      aria-label={`Colonne ${STAGE_LABELS[stage]}`}
    >
      {/* Column header */}
      <div className="flex items-center justify-between px-1">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground truncate">
          {STAGE_LABELS[stage]}
        </h3>
        <span className="ml-1 shrink-0 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-muted text-[10px] font-medium text-muted-foreground">
          {candidates.length}
        </span>
      </div>

      {/* Drop zone */}
      <div
        id={`col-${stage}`}
        data-droppable-id={stage}
        className={cn(
          'flex flex-col gap-2 min-h-[80px] rounded-lg p-2',
          'bg-muted/40 border border-dashed border-border/50'
        )}
      >
        {candidates.length === 0 && (
          <p className="text-[11px] text-muted-foreground text-center py-4">Aucun candidat</p>
        )}
        {candidates.map((c) => (
          <CandidateCard
            key={c.id}
            candidate={c}
            onOpen={onOpen}
            onMove={onMove}
            onArchive={onArchive}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Candidate detail drawer
// ---------------------------------------------------------------------------

interface CandidateDrawerProps {
  candidateId: string | null;
  onClose: () => void;
  filters: CandidateFilters;
  onMove: (id: string, stage: RecruitmentStage) => void;
}

function CandidateDrawer({ candidateId, onClose, filters, onMove }: CandidateDrawerProps) {
  const { data: candidate, isLoading } = useCandidate(candidateId ?? '');
  const updateMutation = useUpdateCandidate(filters);

  const [notes, setNotes] = useState('');
  const [notesKey, setNotesKey] = useState<string | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset local notes when the drawer opens for a different candidate.
  if (candidate && candidate.id !== notesKey) {
    setNotes(candidate.notes ?? '');
    setNotesKey(candidate.id);
  }

  const handleNotesChange = (val: string) => {
    setNotes(val);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (candidate) {
      saveTimer.current = setTimeout(() => {
        updateMutation.mutate({ id: candidate.id, data: { notes: val } });
      }, 800);
    }
  };

  const stageIdx = candidate ? STAGES.indexOf(candidate.stage) : -1;
  const canGoBack = stageIdx > 0 && !isTerminal(candidate?.stage ?? 'embauche');
  const canGoForward =
    candidate && stageIdx < STAGES.length - 1 && !isTerminal(candidate.stage);

  const handleStep = (dir: 'back' | 'forward') => {
    if (!candidate) return;
    const next = dir === 'back' ? STAGES[stageIdx - 1] : STAGES[stageIdx + 1];
    if (next) onMove(candidate.id, next);
  };

  return (
    <Sheet open={!!candidateId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto"
      >
        <SheetHeader className="pr-6">
          <SheetTitle className="text-lg font-semibold truncate">
            {isLoading ? (
              <Skeleton className="h-5 w-48" />
            ) : (
              candidate?.fullName ?? 'Candidat'
            )}
          </SheetTitle>
        </SheetHeader>

        {isLoading && (
          <div className="mt-6 space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-20 w-full" />
          </div>
        )}

        {candidate && (
          <div className="mt-6 space-y-6">
            {/* Stage stepper */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Étape
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleStep('back')}
                  disabled={!canGoBack}
                  aria-label="Étape précédente"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="flex-1 text-center text-sm font-medium px-2 py-1 rounded bg-muted">
                  {STAGE_LABELS[candidate.stage]}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleStep('forward')}
                  disabled={!canGoForward}
                  aria-label="Étape suivante"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              {isTerminal(candidate.stage) && (
                <p className="text-[11px] text-muted-foreground text-center">
                  Étape finale — aucun déplacement possible.
                </p>
              )}
            </div>

            {/* Contact info */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Informations
              </p>
              <dl className="space-y-1.5 text-sm">
                {candidate.email && (
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground w-24 shrink-0">Courriel</dt>
                    <dd className="truncate">
                      <a
                        href={`mailto:${candidate.email}`}
                        className="text-primary hover:underline"
                      >
                        {candidate.email}
                      </a>
                    </dd>
                  </div>
                )}
                {candidate.phone && (
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground w-24 shrink-0">Téléphone</dt>
                    <dd>{candidate.phone}</dd>
                  </div>
                )}
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-24 shrink-0">Source</dt>
                  <dd>
                    <span
                      className={cn(
                        'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border',
                        SOURCE_COLORS[candidate.source]
                      )}
                    >
                      {SOURCE_LABELS[candidate.source]}
                    </span>
                  </dd>
                </div>
                {candidate.score != null && (
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground w-24 shrink-0">Score</dt>
                    <dd className="font-medium">{candidate.score}/10</dd>
                  </div>
                )}
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-24 shrink-0">Postulé le</dt>
                  <dd>
                    {format(new Date(candidate.appliedAt), 'd MMM yyyy', { locale: fr })}
                    {candidate.reappliedAt && (
                      <span className="ml-1 text-orange-600 text-[11px]">
                        (renvoyé{' '}
                        {format(new Date(candidate.reappliedAt), 'd MMM yyyy', {
                          locale: fr,
                        })}
                        )
                      </span>
                    )}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Document links */}
            {(candidate.cvUrl || candidate.questionSetUrl || candidate.debriefUrl) && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Documents
                </p>
                <div className="flex flex-wrap gap-3">
                  {candidate.cvUrl && (
                    <a
                      href={candidate.cvUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                    >
                      <FileText className="h-4 w-4" /> CV
                    </a>
                  )}
                  {candidate.questionSetUrl && (
                    <a
                      href={candidate.questionSetUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                    >
                      <Link2 className="h-4 w-4" /> Questions
                    </a>
                  )}
                  {candidate.debriefUrl && (
                    <a
                      href={candidate.debriefUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                    >
                      <Link2 className="h-4 w-4" /> Débrief
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Notes (autosave) */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Notes
              </p>
              <Textarea
                value={notes}
                onChange={(e) => handleNotesChange(e.target.value)}
                rows={4}
                placeholder="Ajouter des notes sur ce candidat…"
                className="resize-none text-sm"
              />
              {updateMutation.isPending && (
                <p className="text-[11px] text-muted-foreground">Sauvegarde en cours…</p>
              )}
            </div>

            {/* Event history timeline */}
            {candidate.events && candidate.events.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Historique
                </p>
                <ol className="relative border-l border-border ml-2 space-y-4 pb-2">
                  {candidate.events.map((ev) => (
                    <li key={ev.id} className="ml-4">
                      <span
                        aria-hidden="true"
                        className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border border-background bg-muted-foreground"
                      />
                      <p className="text-sm leading-snug">
                        {ev.fromStage ? (
                          <>
                            <span className="font-medium">{STAGE_LABELS[ev.fromStage]}</span>
                            {' → '}
                            <span className="font-medium">{STAGE_LABELS[ev.toStage]}</span>
                          </>
                        ) : (
                          <span className="font-medium">
                            Créé à «&nbsp;{STAGE_LABELS[ev.toStage]}&nbsp;»
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {format(new Date(ev.createdAt), "d MMM yyyy 'à' HH:mm", {
                          locale: fr,
                        })}
                        {ev.note ? ` — ${ev.note}` : ''}
                      </p>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function HrisRecruitmentPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const jobPostingId = searchParams.get('job') ?? '';
  const sourceFilter = (searchParams.get('source') ?? '') as CandidateSource | '';
  const showArchived = searchParams.get('rejetés') === '1';

  const setParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === null || value === '') {
            next.delete(key);
          } else {
            next.set(key, value);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [openCandidateId, setOpenCandidateId] = useState<string | null>(null);
  const [activeCandidate, setActiveCandidate] = useState<Candidate | null>(null);

  const filters: CandidateFilters = {
    jobPostingId: jobPostingId || undefined,
    source: sourceFilter || undefined,
    showArchived,
  };

  const { data: jobsData, isLoading: jobsLoading } = useJobPostings();
  const { data: candidatesData, isLoading: candidatesLoading } = useCandidates(filters);
  const moveCandidate = useMoveCandidate(filters);
  const updateCandidate = useUpdateCandidate(filters);

  const grouped = candidatesData?.candidates;

  // ---------------------------------------------------------------------------
  // Shared handlers passed down to cards (avoids hook calls inside handlers)
  // ---------------------------------------------------------------------------

  const handleMove = useCallback(
    (id: string, stage: RecruitmentStage) => {
      moveCandidate.mutate({ id, stage });
    },
    [moveCandidate]
  );

  const handleArchive = useCallback(
    (id: string, archived: boolean) => {
      updateCandidate.mutate({ id, data: { archived } });
    },
    [updateCandidate]
  );

  // ---------------------------------------------------------------------------
  // dnd-kit sensors
  // ---------------------------------------------------------------------------

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const id = event.active.id as string;
      if (!grouped) return;
      for (const stage of STAGES) {
        const found = grouped[stage]?.find((c) => c.id === id);
        if (found) {
          setActiveCandidate(found);
          break;
        }
      }
    },
    [grouped]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveCandidate(null);

      if (!over || !grouped) return;

      const candidateId = active.id as string;
      const overId = over.id as string;

      // Determine target stage: overId can be a stage key or a candidate id.
      let targetStage: RecruitmentStage | undefined;

      if ((STAGES as readonly string[]).includes(overId)) {
        targetStage = overId as RecruitmentStage;
      } else {
        for (const s of STAGES) {
          if (grouped[s]?.some((c) => c.id === overId)) {
            targetStage = s;
            break;
          }
        }
      }

      if (!targetStage) return;

      // Find source stage.
      let sourceStage: RecruitmentStage | undefined;
      for (const s of STAGES) {
        if (grouped[s]?.some((c) => c.id === candidateId)) {
          sourceStage = s;
          break;
        }
      }

      if (!sourceStage || sourceStage === targetStage) return;

      if (!canTransition(sourceStage, targetStage)) {
        toast.error('Transition non permise', {
          description: "Vous ne pouvez déplacer un candidat que d'une étape à la fois.",
        });
        return;
      }

      handleMove(candidateId, targetStage);
    },
    [grouped, handleMove]
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const jobs = jobsData?.jobPostings ?? [];
  const isEmpty = !jobPostingId;

  return (
    <PageShell subtitle="Gestion des candidatures par étape de recrutement" actions={null}>
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 pb-2">
        {/* Job posting selector */}
        <div className="min-w-[200px]">
          <Select
            value={jobPostingId || '_none'}
            onValueChange={(v) => {
              if (v === '') return;
              setParam('job', v === '_none' ? null : v);
            }}
          >
            <SelectTrigger aria-label="Sélectionner un poste">
              <SelectValue
                placeholder={jobsLoading ? 'Chargement…' : 'Sélectionner un poste'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">— Aucun poste —</SelectItem>
              {jobs.map((j) => (
                <SelectItem key={j.id} value={j.id}>
                  {j.titleFr}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Source filter */}
        <div className="min-w-[160px]">
          <Select
            value={sourceFilter || '_all'}
            onValueChange={(v) => {
              if (v === '') return;
              setParam('source', v === '_all' ? null : v);
            }}
          >
            <SelectTrigger aria-label="Filtrer par source">
              <SelectValue placeholder="Toutes les sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Toutes les sources</SelectItem>
              {(Object.keys(SOURCE_LABELS) as CandidateSource[]).map((s) => (
                <SelectItem key={s} value={s}>
                  {SOURCE_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Show rejected toggle */}
        <div className="flex items-center gap-2">
          <Switch
            id="show-archived"
            checked={showArchived}
            onCheckedChange={(v) => setParam('rejetés', v ? '1' : null)}
          />
          <Label htmlFor="show-archived" className="text-sm cursor-pointer select-none">
            Afficher rejetés
          </Label>
        </div>
      </div>

      {/* Board */}
      {isEmpty ? (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
          <p className="text-sm">Sélectionnez un poste pour afficher le kanban.</p>
        </div>
      ) : candidatesLoading ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {STAGES.map((s) => (
            <div key={s} className="min-w-[200px] space-y-3">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-24 w-full rounded-lg" />
              <Skeleton className="h-16 w-full rounded-lg" />
            </div>
          ))}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div
            className="flex gap-4 overflow-x-auto pb-6 min-h-[60vh]"
            role="region"
            aria-label="Tableau kanban de recrutement"
          >
            {STAGES.map((stage, idx) => {
              let col = grouped?.[stage] ?? [];
              if (!showArchived) col = col.filter((c) => !c.archived);
              return (
                <KanbanColumn
                  key={stage}
                  stage={stage}
                  candidates={col}
                  onOpen={(c) => setOpenCandidateId(c.id)}
                  onMove={handleMove}
                  onArchive={handleArchive}
                  isFirst={idx === 0}
                />
              );
            })}
          </div>

          <DragOverlay dropAnimation={null}>
            {activeCandidate ? <DragCard candidate={activeCandidate} /> : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Candidate detail drawer */}
      <CandidateDrawer
        candidateId={openCandidateId}
        onClose={() => setOpenCandidateId(null)}
        filters={filters}
        onMove={handleMove}
      />
    </PageShell>
  );
}
